import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Printer, RefreshCw } from 'lucide-react';
import { usePrintNode, PrintNodePrinter } from '@/hooks/usePrintNode';

interface PrinterSelectorProps {
  className?: string;
}

export const PrinterSelector: React.FC<PrinterSelectorProps> = ({ className }) => {
  const { 
    printers, 
    selectedPrinter, 
    loading, 
    setSelectedPrinter, 
    fetchPrinters 
  } = usePrintNode();

  // Auto-fetch printers once on mount
  const [hasFetched, setHasFetched] = React.useState(false);
  
  React.useEffect(() => {
    if (!hasFetched && !loading) {
      console.log('PrinterSelector: Fetching printers on mount...');
      setHasFetched(true);
      fetchPrinters();
    }
  }, [hasFetched, loading, fetchPrinters]);

  const handlePrinterChange = (printerId: string) => {
    const printer = printers.find(p => p.id.toString() === printerId);
    if (printer) {
      setSelectedPrinter(printer);
    }
  };

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <Printer className="w-4 h-4 text-gray-600" />
      <div className="flex-1 min-w-0">
        <Select 
          value={selectedPrinter?.id.toString() || ''} 
          onValueChange={handlePrinterChange}
          disabled={loading}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select printer..." />
          </SelectTrigger>
          <SelectContent>
            {printers.map((printer) => (
              <SelectItem key={printer.id} value={printer.id.toString()}>
                <div className="flex items-center gap-2">
                  <span>{printer.name}</span>
                  <span className={`w-2 h-2 rounded-full ${
                    printer.state === 'online' ? 'bg-green-500' : 'bg-red-500'
                  }`} />
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={fetchPrinters}
        disabled={loading}
        className="shrink-0"
      >
        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
      </Button>
    </div>
  );
};